import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { ZodType, z } from 'zod';
import {
  useExperimentTypes,
  usePrivateExperimentTakeover,
} from 'modules/experiments/takeover/hooks';
import { EXPERIMENT_CREATION_FORM_INPUT } from 'modules/experiments/takeover/types';
import { CREATE_EXPERIMENT_SCHEMA } from 'modules/experiments/takeover/schemas/createExperiment';
import { ExperimentCreationModalContext } from './ExperimentCreationModalContext';

// Props
type CreateExperimentFormProviderProps = {
  children: React.ReactNode;
};

export type CreateExperimentFormSchemaType<T extends ZodType<any, any, any>> = Required<z.infer<T>>;

export const CreateExperimentFormProvider = ({ children }: CreateExperimentFormProviderProps) => {
  const {
    campaignInfo: { name: campaignName, id },
  } = usePrivateExperimentTakeover();
  const { fetchExperimentTypes, experimentTypes, ...experimentTypeLoadStates } =
    useExperimentTypes(id);

  const defaultValues = {
    [EXPERIMENT_CREATION_FORM_INPUT.NAME]: `"${campaignName}" experiment`,
    [EXPERIMENT_CREATION_FORM_INPUT.TYPE]: experimentTypes?.[0],
  };
  const methods = useForm<CreateExperimentFormSchemaType<typeof CREATE_EXPERIMENT_SCHEMA>>({
    defaultValues,
    resolver: zodResolver(CREATE_EXPERIMENT_SCHEMA),
    shouldUnregister: true,
    mode: 'all',
  });

  /**
   * The experiment type might not have loaded on time to select the
   * first experiment type by default. So, this fallback ensures to
   * select the first experiment type once it loads.
   */
  useEffect(() => {
    methods.setValue(EXPERIMENT_CREATION_FORM_INPUT.TYPE, experimentTypes?.[0]);
  }, [experimentTypes?.length]);

  /**
   * Fetching the list of experiment types available for
   * a given campaign when the experiment creation modal opens
   */
  useEffect(() => {
    fetchExperimentTypes();
  }, []);

  return (
    <ExperimentCreationModalContext.Provider
      value={{
        ...experimentTypeLoadStates,
        experimentTypes,
      }}
    >
      <FormProvider {...methods}>{children}</FormProvider>
    </ExperimentCreationModalContext.Provider>
  );
};
